import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { ChevronDownIcon, MagnifyingGlassIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@headlessui/react";
import { Listbox } from "@headlessui/react";

export default function MyPermitsPage() {
  const [permits, setPermits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false); // New state for page loading
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState(""); // New state for applied search
  const [currentPage, setCurrentPage] = useState(1);
  const [activationDialog, setActivationDialog] = useState({ open: false, permitId: null, permitVrm: null });
  const [activating, setActivating] = useState(false);
  const [activationError, setActivationError] = useState("");
  const [cancelDialog, setCancelDialog] = useState({ open: false, permitId: null, permitVrm: null });
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [siteSettings, setSiteSettings] = useState(null);
  const location = useLocation();

  const [filters, setFilters] = useState({
    status: ['ACTIVE', 'SCHEDULED', 'QUEUED'],
    startDate: '',
    endDate: ''
  });
  const permitsPerPage = 20;
  const [totalCount, setTotalCount] = useState(0);

  // Fetch site settings on component mount
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const response = await axiosInstance.get('/api/v1/site-settings/');
        setSiteSettings(response.data);
      } catch (error) {
        // Handle error silently
        setSiteSettings({ default_end_date_limit_hours: 0 });
      }
    };
    
    fetchSiteSettings();
  }, []);

  const statusOptions = [
    { id: 'ACTIVE', name: 'Active' },
    { id: 'CANCELLED', name: 'Cancelled' },
    { id: 'EXPIRED', name: 'Expired' },
    { id: 'QUEUED', name: 'Queued' },
    { id: 'SCHEDULED', name: 'Scheduled' },
    { id: 'CANCEL_FAILED', name: 'Cancel Failed' },
    { id: 'CANCEL_PENDING', name: 'Cancel Pending' },
    { id: 'EXPIRE_PENDING', name: 'Expire Pending' },
    { id: 'FAILED', name: 'Failed' },
    { id: 'FAILED_LIMIT', name: 'Failed - Limit Exceeded' },
    { id: 'UPDATE_FAILED', name: 'Update Failed' },
    { id: 'UPDATE_PENDING', name: 'Update Pending' }
  ];

  // Helper function to handle select all functionality
  const handleStatusChange = (newStatuses) => {
    // If "all" is selected, toggle between all statuses and none
    if (newStatuses.includes('all')) {
      if (areAllStatusesSelected()) {
        // If all are selected, deselect all
        setFilters(prev => ({ ...prev, status: [] }));
      } else {
        // If not all are selected, select all
        setFilters(prev => ({ 
          ...prev, 
          status: statusOptions.map(option => option.id)
        }));
      }
    } else {
      // Handle individual status selections
      setFilters(prev => ({ ...prev, status: newStatuses }));
    }
  };

  // Helper function to check if all statuses are selected
  const areAllStatusesSelected = () => {
    return statusOptions.every(option => filters.status.includes(option.id));
  };

  // Helper function to get selected status count
  const getSelectedStatusCount = () => {
    return filters.status.length;
  };

  const fetchPermits = async (showLoading = true, isPageChange = false, pageOverride = null, immediateSearchQuery = null) => {
    try {
      if (showLoading) {
        if (isPageChange) {
          setPageLoading(true); // Use page loading for pagination
        } else {
          setLoading(true); // Use full loading for initial load/filters
        }
      }

      // Build query parameters
      const params = new URLSearchParams();
      if (filters.status.length > 0) {
        // Add each status as a separate parameter
        filters.status.forEach(status => {
          if (status !== 'all') {  // Skip the 'all' value
            params.append('status', status);
          }
        });
      }
      
      // Convert dates to UTC ISO string for backend
      if (filters.startDate) {
        const startDate = new Date(filters.startDate);
        params.append('start_date', startDate.toISOString());
      }
      if (filters.endDate) {
        const endDate = new Date(filters.endDate);
        params.append('end_date', endDate.toISOString());
      }

      // Add search query to backend - use immediateSearchQuery if provided, otherwise use appliedSearchQuery
      const searchQueryToUse = immediateSearchQuery !== null ? immediateSearchQuery : appliedSearchQuery;
      if (searchQueryToUse) {
        params.append('search', searchQueryToUse);
      }

      // Add pagination parameters - use override if provided, otherwise use currentPage
      const pageToUse = pageOverride !== null ? pageOverride : currentPage;
      params.append('page', pageToUse.toString());

      const response = await axiosInstance.get(`/api/v1/permits/?${params.toString()}`);
      
      if (response.data && response.data.error) {
        throw new Error(response.data.message || 'Failed to fetch permits');
      }

      // Handle paginated response
      if (response.data.results) {
        setPermits(response.data.results);
        // Update total count for pagination
        setTotalCount(response.data.count || 0);
      } else {
        // Fallback for non-paginated response
        setPermits(response.data);
        setTotalCount(response.data.length || 0);
      }
      
      setError("");
    } catch (error) {
      // Handle error silently
    } finally {
      if (showLoading) {
        if (isPageChange) {
          setPageLoading(false);
        } else {
          setLoading(false);
        }
      }
    }
  };

  // Initial fetch with default filters
  useEffect(() => {
    fetchPermits(true, false); // Direct fetch on initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on initial load

  // Effect to check for shouldRefresh in location state and refresh permits
  useEffect(() => {
    if (location.state?.shouldRefresh) {
      fetchPermits(true, false);
      // Clear the state to prevent unnecessary refreshes
      window.history.replaceState({}, document.title, location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Effect to handle search with debouncing
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     if (searchQuery !== undefined) { // Only trigger if searchQuery has been set
  //       // Reset to first page and fetch with search
  //       setCurrentPage(1);
  //       fetchPermits(true, true, 1); // Use page loading for search and force page 1
  //       // Scroll to top when search changes
  //       window.scrollTo({ top: 0, behavior: 'smooth' });
  //     }
  //   }, 500); // 500ms debounce

  //   return () => clearTimeout(timeoutId);
  // }, [searchQuery]);

  // Effect to handle page changes
  useEffect(() => {
    // Scroll to top immediately when page changes (before loading)
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchPermits(true, true); // Pass true for pageLoading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page
    setAppliedSearchQuery(searchQuery); // Apply the search query when form is submitted
    fetchPermits(true, true, 1, searchQuery); // Use page loading for filters, force page 1, and pass immediate search query
    // Scroll to top when filters are applied
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = async (permitId, vrm, isExpiryUpdate = false) => {
    if (isExpiryUpdate) {
      // For expiry updates, don't show confirmation dialog
      try {
        await fetchPermits(false);
      } catch (error) {
        // Handle error silently
      }
      return;
    }

    // Find the permit to get the VRM for the dialog
    const permit = permits.find(p => p.id === permitId);
    if (permit) {
      // Clear any previous error when opening a new dialog
      setCancelError("");
      setCancelDialog({ open: true, permitId, permitVrm: permit.vrm });
    }
  };

  const confirmCancel = async () => {
    const { permitId } = cancelDialog;
    setCancelling(true);
    setCancelError("");

    try {
      const response = await axiosInstance.post(`/api/v1/permits/${permitId}/cancel/`);

      if (response.data && response.data.error) {
        throw new Error(response.data.message || 'Failed to cancel permit');
      }
      
      // Add 1 second delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close dialog and refresh permits
      setCancelDialog({ open: false, permitId: null, permitVrm: null });
      await fetchPermits(true, true); // Show loading indicator for refresh
    } catch (error) {
      // Handle error silently
      
      // Handle Django REST Framework validation errors
      let errorMessage = "Failed to cancel permit. Please try again.";
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for non-field errors first (general validation errors)
        if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          errorMessage = errorData.non_field_errors[0];
        }
        // Check for specific field errors
        else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        // Check for legacy error format (single error field)
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Check for message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setCancelError(errorMessage);
    } finally {
      setCancelling(false);
    }
  };

  const handleActivate = async (permitId) => {
    // Find the permit to get the VRM for the dialog
    const permit = permits.find(p => p.id === permitId);
    if (permit) {
      // Clear any previous error when opening a new dialog
      setActivationError("");
      setActivationDialog({ open: true, permitId, permitVrm: permit.vrm });
    }
  };

  const confirmActivate = async () => {
    const { permitId } = activationDialog;
    setActivating(true);
    setActivationError("");

    try {
      const response = await axiosInstance.post(`/api/v1/permits/${permitId}/activate/`);

      if (response.data && response.data.error) {
        throw new Error(response.data.message || 'Failed to activate permit');
      }
      
      // Add 1 second delay for better UX
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Close dialog and refresh permits
      setActivationDialog({ open: false, permitId: null, permitVrm: null });
      await fetchPermits(true, true); // Show loading indicator for refresh
    } catch (error) {
      // Handle error silently
      
      // Handle Django REST Framework validation errors
      let errorMessage = "Failed to activate permit. Please try again.";
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for non-field errors first (general validation errors)
        if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          errorMessage = errorData.non_field_errors[0];
        }
        // Check for specific field errors
        else if (errorData.detail) {
          errorMessage = errorData.detail;
        }
        // Check for legacy error format (single error field)
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Check for message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setActivationError(errorMessage);
    } finally {
      setActivating(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle "Indefinite" string from backend
    if (dateString === 'Indefinite') {
      return 'Indefinite';
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      
      // Check if this is the default "infinite" date (9999-12-01)
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const day = date.getDate();
      
      if (year === 9999 && month === 12 && day === 1) {
        return 'Indefinite';
      }
      
      // Format in UK timezone (local time) with 24-hour format
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Use 24-hour format
        timeZone: 'Europe/London'
      });
    } catch (error) {
      // Handle error silently
      return 'N/A';
    }
  };

  // Remove client-side filtering since it's now handled by backend
  // const filteredPermits = permits.filter(permit => {
  //   // Only apply search query filtering since date filtering is done by backend
  //   if (searchQuery) {
  //     const searchLower = searchQuery.toLowerCase();
  //     const searchableFields = [
  //       permit.vrm,
  //       permit.driver_first,
  //       permit.driver_last,
  //       permit.label || permit.reference_no,
  //       permit.make,
  //       permit.model,
  //       permit.colour,
  //       formatDateTime(permit.created_at),
  //       formatDateTime(permit.end_ts),
  //       permit.status,
  //       permit.driver_first,
  //       permit.driver_last
  //     ].filter(Boolean); // Remove any null/undefined values

  //     const matchesSearch = searchableFields.some(field => 
  //       field.toString().toLowerCase().includes(searchLower)
  //     );
      
  //     if (!matchesSearch) {
  //       return false;
  //     }
  //   }
    
  //   return true;
  // });

  // Use permits directly since filtering is now done by backend
  const currentPermits = permits;
  const totalPages = Math.ceil(totalCount / permitsPerPage);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header Section */}
          <div className="mb-10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Permits</h1>
                <p className="mt-2 text-base text-gray-600">
                  Manage and track your visitor permits
                </p>
              </div>
              <Link to="/add-permit" className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-customIndigo hover:bg-customIndigo/90">
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Permit
              </Link>
            </div>
          </div>

          

          {/* Filters Section */}
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <form className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Status Filter */}
                <div className="lg:col-span-3">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="relative w-full cursor-default rounded-lg bg-white py-2.5 pl-3 pr-10 text-left border border-gray-300">
                    <span className="text-gray-500">Select statuses...</span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </div>
                </div>

                {/* Date Range Filters */}
                <div className="lg:col-span-3">
                  <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Start Date
                    <div className="group relative ml-1">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="font-medium mb-1">Filter by creation date</div>
                        <p className="text-gray-300">Leave empty to show permits from any time</p>
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <input
                    type="datetime-local"
                    id="startDate"
                    value={filters.startDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                  />
                </div>

                <div className="lg:col-span-3">
                  <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    End Date
                    <div className="group relative ml-1">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="font-medium mb-1">Filter by creation date</div>
                        <p className="text-gray-300">Leave empty to show permits from any time</p>
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <input
                    type="datetime-local"
                    id="endDate"
                    value={filters.endDate}
                    onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                  />
                </div>

                {/* Search Filter */}
                <div className="lg:col-span-3">
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Search
                    <div className="group relative ml-1">
                      <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        <div className="font-medium mb-1">Search by:</div>
                        <ul className="space-y-1 text-gray-300">
                          <li>• Vehicle registration (VRM)</li>
                          <li>• Driver first or last name</li>
                          <li>• Reference number or label</li>
                          <li>• Vehicle make, model, or colour</li>
                        </ul>
                        <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                      </div>
                    </div>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      id="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search permits..."
                      title="Search by vehicle registration, driver name, reference number, make, model, or colour"
                      className="mt-1 block w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => {
                          setSearchQuery("");
                          setAppliedSearchQuery("");
                        }}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex justify-end space-x-3">
                <div className="h-10 bg-gray-200 rounded-lg w-20"></div>
                <div className="h-10 bg-gray-200 rounded-lg w-20"></div>
              </div>

              {/* Loading Count Display */}
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
                </div>
              </div>
            </form>
          </div>

          {/* Rest of the page - blank */}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="mb-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Permits</h1>
              <p className="mt-2 text-base text-gray-600">
                Manage and track your visitor permits
              </p>
            </div>
            <Link to="/add-permit" className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-customIndigo hover:bg-customIndigo/90">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Permit
            </Link>
          </div>
        </div>

        {/* Filters Section */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleFilterSubmit} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Status Filter */}
              <div className="lg:col-span-3">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Listbox value={filters.status} onChange={(value) => handleStatusChange(value)} multiple>
                  <div className="relative">
                    <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-2.5 pl-3 pr-10 text-left border border-gray-300 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:border-customIndigo">
                      <span className="block truncate">
                        {filters.status.length === 0 ? 'All statuses' : 
                         areAllStatusesSelected() ? 'All statuses selected' :
                         filters.status.length === 1 ? 
                         statusOptions.find(option => option.id === filters.status[0])?.name || filters.status[0] :
                         `${getSelectedStatusCount()} statuses selected`}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {/* Select All Option */}
                      <Listbox.Option
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-3 pr-9 border-b border-gray-100 ${
                            active ? 'bg-customIndigo text-white' : 'text-gray-900'
                          }`
                        }
                        value="all"
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                              {areAllStatusesSelected() ? 'Deselect All' : 'Select All'}
                            </span>
                            {areAllStatusesSelected() ? (
                              <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                active ? 'text-white' : 'text-customIndigo'
                              }`}>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                </svg>
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                      
                      {/* Individual Status Options */}
                      {statusOptions.map((option) => (
                        <Listbox.Option
                          key={option.id}
                          className={({ active }) =>
                            `relative cursor-default select-none py-2 pl-3 pr-9 ${
                              active ? 'bg-customIndigo text-white' : 'text-gray-900'
                            }`
                          }
                          value={option.id}
                        >
                          {({ selected, active }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {option.name}
                              </span>
                              {selected ? (
                                <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                                  active ? 'text-white' : 'text-customIndigo'
                                }`}>
                                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                  </svg>
                                </span>
                              ) : null}
                            </>
                          )}
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </div>
                </Listbox>
              </div>

              {/* Date Range Filters */}
              <div className="lg:col-span-3">
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Start Date
                  <div className="group relative ml-1">
                    <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-medium mb-1">Filter by creation date</div>
                      <p className="text-gray-300">Leave empty to show permits from any time</p>
                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </label>
                <input
                  type="datetime-local"
                  id="startDate"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                />
              </div>

              <div className="lg:col-span-3">
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  End Date
                  <div className="group relative ml-1">
                    <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-0 mb-2 w-48 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-medium mb-1">Filter by creation date</div>
                      <p className="text-gray-300">Leave empty to show permits from any time</p>
                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </label>
                <input
                  type="datetime-local"
                  id="endDate"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                />
              </div>

              {/* Search Filter */}
              <div className="lg:col-span-3">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Search
                  <div className="group relative ml-1">
                    <svg className="h-4 w-4 text-gray-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-0 mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                      <div className="font-medium mb-1">Search by:</div>
                      <ul className="space-y-1 text-gray-300">
                        <li>• Vehicle registration (VRM)</li>
                        <li>• Driver first or last name</li>
                        <li>• Reference number or label</li>
                        <li>• Vehicle make, model, or colour</li>
                      </ul>
                      <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                    </div>
                  </div>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search permits..."
                    title="Search by vehicle registration, driver name, reference number, make, model, or colour"
                    className="mt-1 block w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-1 focus:ring-customIndigo"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery("");
                        setAppliedSearchQuery("");
                      }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    status: ['ACTIVE', 'SCHEDULED', 'QUEUED'],
                    startDate: '',
                    endDate: ''
                  });
                  setSearchQuery(""); // Clear the search input
                  setAppliedSearchQuery(""); // Clear the applied search
                  setCurrentPage(1); // Reset to first page
                  // Scroll to top when reset is applied
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  // Fetch with reset filters
                  fetchPermits(true, true, 1, ""); // Use page loading, force page 1, clear search
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:border-customIndigo"
              >
                Reset
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-customIndigo border border-transparent rounded-md hover:bg-customIndigo/90 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2"
              >
                Search
              </button>
            </div>

            {/* Permit Count Display */}
            {!loading && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-customIndigo rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700">
                      {totalCount === 0 ? 'No permits found' : 
                       totalCount === 1 ? '1 permit found' : 
                       `${totalCount.toLocaleString()} permits found`}
                    </span>
                    {currentPermits.length > 0 && totalCount > currentPermits.length && (
                      <span className="text-sm text-gray-500">
                        (showing {((currentPage - 1) * permitsPerPage) + 1}-{Math.min(currentPage * permitsPerPage, totalCount)} of {totalCount.toLocaleString()})
                      </span>
                    )}
                  </div>
                  {totalCount > 0 && (
                    <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                      Page {currentPage} of {totalPages}
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Permits List */}
        <div className="bg-white shadow-sm rounded-lg">
          {/* Page Loading Indicator */}
          {pageLoading && (
            <div className="px-6 py-3 bg-customIndigo/5 border-b border-customIndigo/20">
              <div className="flex items-center justify-center">
                <svg className="animate-spin h-4 w-4 text-customIndigo mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-customIndigo font-medium">Loading...</span>
              </div>
            </div>
          )}
          
          {currentPermits.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No permits found matching your criteria.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {/* Desktop Table Headers */}
              <div className="hidden lg:block">
                <div className="grid grid-cols-6 gap-0 px-6 py-4 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-700">
                  <div className="col-span-1 border-r border-gray-200 px-4">Vehicle & Details</div>
                  <div className="col-span-1 border-r border-gray-200 px-4 text-center">Status</div>
                  <div className="col-span-1 border-r border-gray-200 px-4 text-center">Driver</div>
                  <div className="col-span-2 border-r border-gray-200 px-4 text-center">Timing</div>
                  <div className="col-span-1 px-4 text-center">Actions</div>
                </div>
              </div>
              
              {currentPermits.map((permit) => (
                <PermitListItem
                  key={permit.id}
                  permit={permit}
                  onCancel={handleCancel}
                  formatDateTime={formatDateTime}
                  handleActivate={handleActivate}
                  fetchPermits={fetchPermits}
                  siteSettings={siteSettings}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    page === currentPage
                      ? 'text-white bg-customIndigo border border-customIndigo'
                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Activation Confirmation Dialog */}
      <Dialog open={activationDialog.open} onClose={() => setActivationDialog({ open: false, permitId: null, permitVrm: null })} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-2xl bg-white shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <Dialog.Title className="text-xl font-bold text-gray-900">
                    Activate Permit
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Vehicle: {activationDialog.permitVrm?.toUpperCase()}
                  </p>
                </div>
                <button
                  onClick={() => setActivationDialog({ open: false, permitId: null, permitVrm: null })}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-6">
              {activationError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Activation Failed</p>
                      <p className="mt-1">{activationError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Activate Scheduled Permit?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  This will immediately activate the scheduled permit for vehicle <strong>{activationDialog.permitVrm?.toUpperCase()}</strong>. 
                  The permit will become active and start immediately.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-5 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setActivationDialog({ open: false, permitId: null, permitVrm: null })}
                disabled={activating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmActivate}
                disabled={activating}
                className="px-5 py-2.5 text-sm font-medium text-white bg-green-600 border border-transparent rounded-xl hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
              >
                {activating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Activating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    Activate Permit
                  </>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialog.open} onClose={() => setCancelDialog({ open: false, permitId: null, permitVrm: null })} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-md w-full rounded-2xl bg-white shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-orange-50 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <Dialog.Title className="text-xl font-bold text-gray-900">
                    Cancel Permit
                  </Dialog.Title>
                  <p className="mt-1 text-sm text-gray-600">
                    Vehicle: {cancelDialog.permitVrm?.toUpperCase()}
                  </p>
                </div>
                <button
                  onClick={() => setCancelDialog({ open: false, permitId: null, permitVrm: null })}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-6 py-6">
              {cancelError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Cancellation Failed</p>
                      <p className="mt-1">{cancelError}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Cancel Permit?
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure you want to cancel the permit for vehicle <strong>{cancelDialog.permitVrm?.toUpperCase()}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-5 bg-gray-50 rounded-b-2xl flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setCancelDialog({ open: false, permitId: null, permitVrm: null })}
                disabled={cancelling}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 transition-all duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmCancel}
                disabled={cancelling}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
              >
                {cancelling ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Cancelling...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Cancel Permit
                  </>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}

function PermitListItem({ permit, onCancel, formatDateTime, handleActivate, fetchPermits, siteSettings }) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState("");
  const [newStartDate, setNewStartDate] = useState("");
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [isIndefinite, setIsIndefinite] = useState(false);

  // Function to convert permit date to datetime-local format
  const convertToDateTimeLocal = (dateString) => {
    if (!dateString || dateString === 'Indefinite') {
      return "";
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      
      // Check if this is the default "infinite" date (9999-12-01)
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      
      if (year === 9999 && month === 12 && day === 1) {
        return ""; // Return empty for indefinite
      }
      
      // Format as YYYY-MM-DDTHH:MM for datetime-local input
      const yearStr = date.getFullYear();
      const monthStr = String(date.getMonth() + 1).padStart(2, '0');
      const dayStr = String(date.getDate()).padStart(2, '0');
      const hoursStr = String(date.getHours()).padStart(2, '0');
      const minutesStr = String(date.getMinutes()).padStart(2, '0');
      
      return `${yearStr}-${monthStr}-${dayStr}T${hoursStr}:${minutesStr}`;
    } catch (error) {
      // Handle error silently
      return "";
    }
  };

  // Function to handle closing the edit modal
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    // Reset form fields
    setNewStartDate("");
    setNewExpiryDate("");
    setIsIndefinite(false);
    setError("");
  };

  // Function to handle opening the edit modal
  const openEditModal = () => {
    // Pre-populate the date fields with current permit dates
    setNewStartDate(convertToDateTimeLocal(permit.start_ts));
    setNewExpiryDate(convertToDateTimeLocal(permit.end_ts));
    setIsIndefinite(permit.end_ts === 'Indefinite');
    setError("");
    setIsEditModalOpen(true);
  };

  const handleUpdatePermit = async () => {
    // For scheduled permits, require end date only if site doesn't have default end date limit
    if (!siteSettings?.default_end_date_limit_hours && !newExpiryDate && !isIndefinite) {
      setError("Please select a new expiry date or mark as indefinite");
      return;
    }

    setIsUpdating(true);
    setError("");

    try {
      let response;
      let updateData = {};
      
      // For scheduled permits, use full update endpoint
      updateData = {};
      
      // Add start date if provided and different from current
      if (newStartDate) {
        updateData.start_ts = newStartDate;
      }
      
      // Add end date if provided and different from current, but only if site allows custom end dates
      if (!siteSettings?.default_end_date_limit_hours && (newExpiryDate || isIndefinite)) {
        updateData.end_ts = isIndefinite ? null : newExpiryDate;
      }
      
      response = await axiosInstance.patch(`/api/v1/permits/${permit.id}/full_update/`, updateData);

      if (response.data && response.data.error) {
        throw new Error(response.data.message || 'Failed to update permit');
      }

      onCancel(permit.id, permit.vrm, true);
      setIsEditModalOpen(false);
      fetchPermits(false); // Quick refresh without loading indicator
    } catch (error) {
      // Handle error silently
      
      // Handle Django REST Framework validation errors
      let errorMessage = "Failed to update permit. Please try again.";
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check for non-field errors first (general validation errors)
        if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
          errorMessage = errorData.non_field_errors[0];
        }
        // Check for specific field errors
        else if (errorData.end_ts) {
          errorMessage = errorData.end_ts[0];
        }
        else if (errorData.start_ts) {
          errorMessage = errorData.start_ts[0];
        }
        // Check for legacy error format (single error field)
        else if (errorData.error) {
          errorMessage = errorData.error;
        }
        // Check for message field
        else if (errorData.message) {
          errorMessage = errorData.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'QUEUED':
        return 'bg-gray-100 text-gray-800'; // grey spinner
      case 'CANCEL_PENDING':
        return 'bg-orange-100 text-orange-800'; // orange/grey
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800'; // grey, no actions
      case 'CANCEL_FAILED':
        return 'bg-red-100 text-red-800'; // red, enable Retry btn
      case 'UPDATE_PENDING':
        return 'bg-orange-100 text-orange-800'; // orange/grey
      case 'UPDATE_FAILED':
        return 'bg-red-100 text-red-800'; // red, enable Retry btn
      case 'EXPIRE_PENDING':
        return 'bg-yellow-100 text-yellow-800'; // yellow
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-800'; // grey, no actions
      case 'FAILED':
        return 'bg-red-100 text-red-800'; // red, enable Retry btn
      case 'FAILED_LIMIT':
        return 'bg-purple-100 text-purple-800'; // purple
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusDisplayName = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'SCHEDULED':
        return 'Scheduled';
      case 'QUEUED':
        return 'Queued';
      case 'CANCEL_PENDING':
        return 'Cancelling…';
      case 'CANCELLED':
        return 'Cancelled';
      case 'CANCEL_FAILED':
        return 'Cancel failed';
      case 'UPDATE_PENDING':
        return 'Updating…';
      case 'UPDATE_FAILED':
        return 'Update Failed';
      case 'EXPIRE_PENDING':
        return 'Expiring…';
      case 'EXPIRED':
        return 'Expired';
      case 'FAILED':
        return 'Failed';
      case 'FAILED_LIMIT':
        return 'Failed - Limit Exceeded';
      default:
        return status;
    }
  };

  return (
    <div className="p-6 border-b border-gray-200">
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        {/* Table Row */}
        <div className="grid grid-cols-6 gap-0 px-6 py-4 items-center text-sm hover:bg-gray-50 transition-colors">
          {/* Vehicle & Details */}
          <div className="col-span-1 border-r border-gray-200 px-4">
            <div className="space-y-2">
              <div className="font-semibold text-gray-900 text-base">{permit.vrm.toUpperCase()}</div>
              <div className="text-sm text-gray-600">
                {permit.make} {permit.model} {permit.colour}
              </div>
              <div className="text-sm text-gray-600">
                Ref: {permit.label ? permit.reference_no : (permit.reference_no || 'N/A')}
              </div>
            </div>
          </div>
          
          {/* Status */}
          <div className="col-span-1 border-r border-gray-200 px-4 text-center">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`}>
              {getStatusDisplayName(permit.status)}
            </span>
          </div>
          
          {/* Driver */}
          <div className="col-span-1 border-r border-gray-200 px-4 text-center">
            <div className="text-sm text-gray-600">
              {(permit.driver_first || permit.driver_last) ? (
                <div>
                  {permit.driver_first} {permit.driver_last}
                </div>
              ) : (
                <div className="text-gray-400">No driver</div>
              )}
            </div>
          </div>
          
          {/* Timing */}
          <div className="col-span-2 border-r border-gray-200 px-4 text-center">
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-center">
                <span className="font-medium text-gray-700 mr-2">Start:</span>
                <span className="text-gray-600">{formatDateTime(permit.start_ts)}</span>
              </div>
              <div className="flex items-center justify-center">
                <span className="font-medium text-gray-700 mr-2">End:</span>
                <span className="text-gray-600">{formatDateTime(permit.end_ts)}</span>
              </div>
            </div>
          </div>
          
          {/* Actions */}
          <div className="col-span-1 px-4 text-center">
            <div className="flex flex-col items-center space-y-2">
              {/* Update button for SCHEDULED permits only */}
              {permit.status === 'SCHEDULED' && (
                <button
                  onClick={openEditModal}
                  className="w-full px-3 py-1.5 text-xs font-medium text-customIndigo border border-customIndigo rounded hover:bg-customIndigo hover:text-white transition-colors"
                >
                  Update
                </button>
              )}
              
              {/* Set Live Now button for SCHEDULED permits */}
              {permit.status === 'SCHEDULED' && (
                <button
                  onClick={() => handleActivate(permit.id)}
                  className="w-full px-3 py-1.5 text-xs font-medium text-green-600 border border-green-600 rounded hover:bg-green-600 hover:text-white transition-colors"
                >
                  Set Live Now
                </button>
              )}
              
              {/* Cancel button for ACTIVE, QUEUED, SCHEDULED, UPDATE_PENDING, and UPDATE_FAILED permits */}
              {(permit.status === 'ACTIVE' || permit.status === 'QUEUED' || permit.status === 'SCHEDULED' || permit.status === 'UPDATE_PENDING' || permit.status === 'UPDATE_FAILED') && (
                <button
                  onClick={() => onCancel(permit.id, permit.vrm)}
                  className="w-full px-3 py-1.5 text-xs font-medium text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden">
        {/* Compact Header with all essential info */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="text-base font-medium text-gray-900">
                {permit.vrm.toUpperCase()}
              </h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`}>
                {getStatusDisplayName(permit.status)}
              </span>
            </div>
            
            <div className="space-y-1 text-sm text-gray-600">
              <div>
                <span className="font-medium">Ref:</span> {permit.label ? permit.reference_no : (permit.reference_no || 'N/A')}
              </div>
              {(permit.driver_first || permit.driver_last) && (
                <div>
                  <span className="font-medium">Driver:</span> {permit.driver_first} {permit.driver_last}
                </div>
              )}
              <div>
                <span className="font-medium">Start:</span> {formatDateTime(permit.start_ts)}
              </div>
              <div>
                <span className="font-medium">End:</span> {formatDateTime(permit.end_ts)}
              </div>
            </div>
          </div>
          
          {/* Action Buttons - Small boxes in top right */}
          <div className="flex flex-col space-y-1 ml-3 min-w-[60px]">
            {/* Update button for SCHEDULED permits only */}
            {permit.status === 'SCHEDULED' && (
              <button
                onClick={openEditModal}
                className="px-2 py-1 text-xs font-medium text-customIndigo border border-customIndigo rounded hover:bg-customIndigo hover:text-white transition-colors"
                title="Update permit"
              >
                Update
              </button>
            )}
            
            {/* Set Live Now button for SCHEDULED permits */}
            {permit.status === 'SCHEDULED' && (
              <button
                onClick={() => handleActivate(permit.id)}
                className="px-2 py-1 text-xs font-medium text-green-600 border border-green-600 rounded hover:bg-green-600 hover:text-white transition-colors"
                title="Set Live Now"
              >
                Set Live Now
              </button>
            )}
            
            {/* Cancel button for ACTIVE, QUEUED, SCHEDULED, UPDATE_PENDING, and UPDATE_FAILED permits */}
            {(permit.status === 'ACTIVE' || permit.status === 'QUEUED' || permit.status === 'SCHEDULED' || permit.status === 'UPDATE_PENDING' || permit.status === 'UPDATE_FAILED') && (
              <button
                onClick={() => onCancel(permit.id, permit.vrm)}
                className="px-2 py-1 text-xs font-medium text-red-600 border border-red-600 rounded hover:bg-red-600 hover:text-white transition-colors"
                title="Cancel permit"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Update Modal */}
      <Dialog open={isEditModalOpen} onClose={closeEditModal} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="mx-auto max-w-lg w-full rounded-2xl bg-white shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 bg-gradient-to-r from-customIndigo/5 to-blue-50/50 sticky top-0 bg-white z-10">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <Dialog.Title className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                    {permit.status === 'SCHEDULED' ? 'Update Scheduled Permit' : 'Update Permit Expiry'}
                  </Dialog.Title>
                  <p className="mt-1 text-xs sm:text-sm text-gray-600 truncate">
                    Vehicle: {permit.vrm.toUpperCase()} • Reference: {permit.label ? permit.reference_no : (permit.reference_no || 'N/A')}
                  </p>
                </div>
                <button
                  onClick={closeEditModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 ml-2 flex-shrink-0"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="px-4 sm:px-6 py-4 sm:py-6">
              {error && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs sm:text-sm">
                  <div className="flex items-start">
                    <svg className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="font-medium">Update Failed</p>
                      <p className="mt-1">{error}</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4 sm:space-y-6">
                {/* Current Status */}
                <div className="p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Current Permit Details
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Start Date:</span>
                      <p className="text-gray-600 truncate">{formatDateTime(permit.start_ts)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Current Expiry:</span>
                      <p className="text-gray-600 truncate">{formatDateTime(permit.end_ts)}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="font-medium text-gray-700">Status:</span>
                      <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(permit.status)}`}>
                        {getStatusDisplayName(permit.status)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* New Expiry Options */}
                <div className="space-y-3 sm:space-y-4">
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-900 flex items-center">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 text-customIndigo" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {permit.status === 'SCHEDULED' ? 'Update Schedule' : 'Set New Expiry'}
                  </h4>
                  
                  {/* Start Date Input - Only for scheduled permits */}
                  {permit.status === 'SCHEDULED' && (
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                        New Start Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={newStartDate}
                        onChange={(e) => setNewStartDate(e.target.value)}
                        className="block w-full rounded-lg sm:rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                        placeholder="Select date and time"
                      />
                      <p className="mt-1 sm:mt-2 text-xs text-gray-500 flex items-center">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Choose a future date and time for when the permit should activate
                      </p>
                    </div>
                  )}
                  
                  {/* End Date Section - Only show if site doesn't have default end date limit */}
                  {!siteSettings?.default_end_date_limit_hours && (
                    <>
                      {/* Date/Time Input */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                          {permit.status === 'SCHEDULED' ? 'New End Date & Time' : 'New Expiry Date & Time'}
                        </label>
                        <input
                          type="datetime-local"
                          value={newExpiryDate}
                          onChange={(e) => {
                            setNewExpiryDate(e.target.value);
                            if (e.target.value) {
                              setIsIndefinite(false); // Clear indefinite when date is selected
                            }
                          }}
                          className="block w-full rounded-lg sm:rounded-xl border border-gray-300 px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm shadow-sm focus:border-customIndigo focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-opacity-50 transition-all duration-200"
                          placeholder="Select date and time"
                        />
                        <p className="mt-1 sm:mt-2 text-xs text-gray-500 flex items-center">
                          <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {permit.status === 'SCHEDULED' 
                            ? 'Choose when the permit should expire'
                            : 'Choose a future date and time for the permit expiry'
                          }
                        </p>
                      </div>
                      
                      {/* Indefinite Option */}
                      <div className="flex items-start space-x-2 sm:space-x-3 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl">
                        <input
                          type="checkbox"
                          id={`indefinite-${permit.id}`}
                          checked={isIndefinite}
                          onChange={(e) => {
                            setIsIndefinite(e.target.checked);
                            if (e.target.checked) {
                              setNewExpiryDate(""); // Clear the date input when indefinite is selected
                            }
                          }}
                          className="h-4 w-4 text-customIndigo focus:ring-customIndigo border-gray-300 rounded mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`indefinite-${permit.id}`} className="block text-xs sm:text-sm font-semibold text-blue-900">
                            Set as Indefinite
                          </label>
                          <p className="text-xs text-blue-700 mt-1">
                            Remove expiry date - permit will remain active until manually cancelled
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Default End Date Info - Show when site has default end date limit */}
                  {siteSettings?.default_end_date_limit_hours > 0 && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-semibold text-blue-900">
                            Automatic End Date
                          </h3>
                          <p className="text-xs text-blue-700 mt-1">
                            {permit.status === 'SCHEDULED' 
                              ? `When you update the start date, the end date will automatically be set to ${siteSettings.default_end_date_limit_hours} hour${siteSettings.default_end_date_limit_hours !== 1 ? 's' : ''} after the new start time.`
                              : `Permits automatically expire ${siteSettings.default_end_date_limit_hours} hour${siteSettings.default_end_date_limit_hours !== 1 ? 's' : ''} after the start time. End dates cannot be modified.`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gray-50 rounded-b-2xl flex justify-end space-x-2 sm:space-x-3 sticky bottom-0 bg-gray-50 z-10">
              <button
                type="button"
                onClick={closeEditModal}
                className="px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg sm:rounded-xl hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdatePermit}
                disabled={isUpdating || (permit.status === 'SCHEDULED' ? (!newStartDate && !newExpiryDate && !isIndefinite) : (!siteSettings?.default_end_date_limit_hours && !newExpiryDate && !isIndefinite))}
                className="px-3 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-customIndigo border border-transparent rounded-lg sm:rounded-xl hover:bg-customIndigo/90 focus:outline-none focus:ring-2 focus:ring-customIndigo focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center"
              >
                {isUpdating ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="hidden sm:inline">{permit.status === 'SCHEDULED' ? 'Updating Schedule...' : 'Updating Expiry...'}</span>
                    <span className="sm:hidden">Updating...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span className="hidden sm:inline">{permit.status === 'SCHEDULED' ? 'Update Schedule' : 'Update Expiry'}</span>
                    <span className="sm:hidden">Update</span>
                  </>
                )}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
}
